<template>
    <!-- Meetings Home (from ironbook-proto.html meetingsHome): the week ahead, each group at a glance, and what's on your plate.
         Data from GET /api/meetings/home; when meetings happen comes from meetingTime.ts, shown in the viewer's local time. -->
    <div v-if="loaded" class="mh">
        <!-- The Forge's Daily Digest: today's meetings, what's urgent, and suggested dates for anything undated -->
        <forge-daily-digest in-meetings @open-meeting="$emit('open-meeting', $event)" @open-item="$emit('open-item', $event)" />
        <div class="mh-grid">
            <div class="mh-col">
                <div class="mh-card">
                    <div class="mh-h">
                        <h3><svg-icon icon="calendar" />My Week</h3>
                        <div class="seg">
                            <button type="button" :class="scope == 'mine' ? 'on' : ''" @click="scope = 'mine'">My Meetings ({{ weekMine.length }})</button>
                            <button type="button" :class="scope == 'all' ? 'on' : ''" @click="scope = 'all'">All Teams ({{ weekAll.length }})</button>
                        </div>
                    </div>
                    <!-- Keelan, 2 Nov 2026 (fewer Forge entry points): the Forge picker that was here is gone; each meeting has
                         one button instead, "Prep for my meeting" / "Improve my meeting" (Meetings.vue). -->
                    <div v-for="day in weekDays" :key="day.offset" class="mh-day">
                        <div class="mh-dl" :class="day.offset == 0 ? 'today' : ''">{{ day.label }}<span v-if="day.sub">{{ day.sub }}</span></div>
                        <div v-for="row in day.rows" :key="row.key" class="mh-row" :class="row.ended ? 'mh-row-ended' : ''">
                            <span class="mh-time">{{ row.time }}</span>
                            <svg v-if="!row.meeting.Team" class="mh-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
                            <span v-else class="mh-dot" :style="{ background: row.color }"></span>
                            <div class="mh-mm">
                                <b>{{ row.meeting.Name }}</b>
                                <span>{{ row.groupName }} · {{ recurrenceLabel(row.meeting.RecurrenceType) }}<template v-if="row.zoneNote"> · {{ row.zoneNote }}</template></span>
                            </div>
                            <button type="button" class="btn-light mh-open" @click="$emit('open-meeting', row.meeting)">Open</button>
                        </div>
                        <p v-if="!day.rows.length" class="mh-none">{{ scope == 'mine' ? 'No meetings for you today.' : 'No meetings today.' }}</p>
                    </div>
                    <p v-if="!weekRows.length" class="mh-none">{{ scope == 'mine' ? "You don't have any meetings in the next 7 days." : 'No meetings in the next 7 days.' }}</p>
                </div>

                <div class="mh-h2">Teams</div>
                <div class="mh-wsgrid">
                    <button v-for="g in teamCards" :key="g.key" type="button" class="mh-ws" @click="$emit('open-group', g.key)">
                        <div class="mh-ws-h">
                            <svg v-if="g.key == 'my'" class="mh-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
                            <span v-else class="mh-dot" :style="{ background: g.color }"></span>
                            <b>{{ g.name }}</b>
                            <svg-icon icon="chevright" class="mh-chev" />
                        </div>
                        <div class="mh-ws-m">
                            <!-- A preview: the 3 soonest, then a count of the rest -->
                            <span v-for="m in g.meetings.slice(0, 3)" :key="m.ID">{{ m.Name }}</span>
                            <span v-if="g.meetings.length > 3" class="mh-more">+{{ g.meetings.length - 3 }} more</span>
                            <span v-if="!g.meetings.length" class="mh-muted">No meetings yet</span>
                        </div>
                        <div class="mh-ws-s">
                            <span><b>{{ g.openItems }}</b> open item{{ g.openItems == 1 ? '' : 's' }}</span>
                            <span v-if="g.next" class="mh-next"><svg-icon icon="clock" />{{ g.next }}</span>
                        </div>
                    </button>
                </div>
            </div>

            <div class="mh-col">
                <div class="mh-card">
                    <div class="mh-h"><h3><svg-icon icon="user" />On Your Plate</h3></div>
                    <template v-if="overdue.length">
                        <div class="mh-sub">Overdue <span>{{ overdue.length }}</span></div>
                        <button v-for="i in overdue" :key="i.ID" type="button" class="mh-item" @click="$emit('open-item', i)">
                            <div class="mh-mm"><b>{{ i.Name }}</b><span>{{ i.MeetingName }} · <em class="mh-overdue">due {{ dueLabel(i.DueDate) }}</em></span></div>
                        </button>
                    </template>
                    <div class="mh-sub">{{ overdue.length ? 'Coming up' : 'Assigned to you' }} <span>{{ upcoming.length }}</span></div>
                    <button v-for="i in upcoming" :key="i.ID" type="button" class="mh-item" @click="$emit('open-item', i)">
                        <div class="mh-mm"><b>{{ i.Name }}</b><span>{{ i.MeetingName }}<template v-if="i.DueDate"> · due {{ dueLabel(i.DueDate) }}</template></span></div>
                    </button>
                    <p v-if="!plate.length" class="mh-none">Nothing open is assigned to you.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import SvgIcon from './SvgIcon.vue';
import ForgeDailyDigest from './ForgeDailyDigest.vue';
import { occurrencesInRange, nextOccurrence, occurrenceLocal, occurrenceSortKey, occurrenceTimeLabel, hasTime, browserTimeZone } from '@/meetingTime';

const RECURRENCE_LABELS: any = { once: 'One time', daily: 'Daily', weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly' };
// My Meetings' color (its dots in My Week): the company's primary color
const PERSONAL_COLOR = 'var(--primary-color)';

function dayStart(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export default defineComponent({
    name: 'MeetingsHome',
    emits: [ 'open-meeting', 'open-group', 'open-item' ],
    components: {
        SvgIcon, ForgeDailyDigest
    },
    data() {
        return {
            loaded: false,
            scope: 'mine' as 'mine' | 'all',
            groups: [] as any[],
            meetings: [] as any[],
            plate: [] as any[],
            now: new Date()
        }
    },
    computed: {
        groupColor(): (meeting: any) => string {
            return (meeting: any) => meeting.Team ? (this.groups.find((g: any) => g.name == meeting.Team)?.color || '#888') : PERSONAL_COLOR;
        },
        // Every meeting in the next 7 days (today through 6 days out), today's finished ones included
        weekAll(): any[] {
            const from = dayStart(this.now);
            const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 7);
            const rows: any[] = [];
            for(const meeting of this.meetings) {
                for(const o of occurrencesInRange(meeting, from, to)) {
                    const local = occurrenceLocal(o);
                    rows.push({
                        key: `${meeting.ID}-${local.getTime()}`,
                        meeting,
                        offset: Math.round((dayStart(local).getTime() - from.getTime()) / 86400000),
                        sort: local.getTime(),
                        // Just the viewer's time here; the meeting's own zone, when different, is in zoneNote
                        time: o.start ? o.start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'No time',
                        zoneNote: this.zoneNote(meeting, o),
                        ended: !!o.end && o.end.getTime() <= this.now.getTime(),
                        color: this.groupColor(meeting),
                        groupName: meeting.Team || 'My Meetings'
                    });
                }
            }
            return rows.sort((a, b) => a.sort - b.sort);
        },
        weekMine(): any[] {
            return this.weekAll.filter((row: any) => row.meeting.IsMine);
        },
        weekRows(): any[] {
            return this.scope == 'mine' ? this.weekMine : this.weekAll;
        },
        // Today always shows (even empty); later days only when they have meetings
        weekDays(): any[] {
            const days: any[] = [];
            for(let offset = 0; offset < 7; offset++) {
                const rows = this.weekRows.filter((row: any) => row.offset == offset);
                if(offset > 0 && !rows.length) {
                    continue;
                }
                const d = new Date(this.now.getFullYear(), this.now.getMonth(), this.now.getDate() + offset);
                days.push({
                    offset,
                    rows,
                    label: offset == 0 ? 'Today' : offset == 1 ? 'Tomorrow' : d.toLocaleDateString(undefined, { weekday: 'long' }),
                    sub: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                });
            }
            return days;
        },
        // My Meetings first, then each group in the company's order; each with its meetings, open items and next meeting
        teamCards(): any[] {
            const cards = [ { key: 'my', name: 'My Meetings', color: PERSONAL_COLOR, team: null }, ...this.groups.map((g: any) => ({ key: g.name, name: g.name, color: g.color, team: g.name })) ];
            return cards.map(card => {
                // Soonest next occurrence first, so the card's preview shows what's coming up
                const meetings = this.meetings.filter((m: any) => (m.Team || null) == card.team)
                    .sort((a: any, b: any) => occurrenceSortKey(nextOccurrence(a, this.now)) - occurrenceSortKey(nextOccurrence(b, this.now)));
                let next: any = null;
                for(const m of meetings) {
                    const o = nextOccurrence(m, this.now);
                    const local = occurrenceLocal(o);
                    if(local.getTime() >= dayStart(this.now).getTime() && (!next || local.getTime() < next.time)) {
                        next = { time: local.getTime(), label: this.nextLabel(m, o) };
                    }
                }
                return {
                    ...card,
                    meetings,
                    openItems: meetings.reduce((sum: number, m: any) => sum + (m.OpenItems || 0), 0),
                    next: next ? next.label : ''
                };
            });
        },
        today(): string {
            const d = this.now;
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        },
        overdue(): any[] {
            return this.plate.filter((i: any) => i.DueDate && i.DueDate < this.today);
        },
        upcoming(): any[] {
            return this.plate.filter((i: any) => !i.DueDate || i.DueDate >= this.today);
        }
    },
    async created() {
        await this.load();
    },
    methods: {
        async load() {
            await axios.get(`${this.$store.state.apiUrl}/api/meetings/home`)
                .then(resp => {
                    this.groups = resp.data.Groups;
                    this.meetings = resp.data.Meetings;
                    this.plate = resp.data.Plate;
                    this.now = new Date();
                })
                .catch(e => {
                    console.log(e);
                })
            this.loaded = true;
        },
        recurrenceLabel(type: string): string {
            return RECURRENCE_LABELS[type] || type;
        },
        // "9:00 AM CT", when the meeting's zone differs from the viewer's
        zoneNote(meeting: any, o: any): string {
            if(!o.start || !hasTime(meeting) || meeting.TimeZone == browserTimeZone()) {
                return '';
            }
            const label = occurrenceTimeLabel(meeting, o);
            const own = label.match(/\((.*)\)$/);
            return own ? own[1] : '';
        },
        // "Today 2:00 PM", "Tomorrow", "Thu 9:00 AM", "Oct 30"
        nextLabel(meeting: any, o: any): string {
            const local = occurrenceLocal(o);
            const offset = Math.round((dayStart(local).getTime() - dayStart(this.now).getTime()) / 86400000);
            const day = offset == 0 ? 'Today' : offset == 1 ? 'Tomorrow' : offset < 7 ? local.toLocaleDateString(undefined, { weekday: 'short' }) : local.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
            return o.start ? `${day} ${o.start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : day;
        },
        dueLabel(date: string): string {
            if(date == this.today) {
                return 'today';
            }
            const [ y, m, d ] = date.split('-').map(n => parseInt(n));
            return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: y != this.now.getFullYear() ? 'numeric' : undefined });
        }
    }
});
</script>

<style scoped>
/* Same 24px sides as the meeting header and empty state (Meetings.vue) */
.mh { padding: 20px 24px 30px; }
@media(max-width: 600px) { .mh { padding: 16px; } }
.mh-grid { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 16px; align-items: start; }
@media(max-width: 1100px) { .mh-grid { grid-template-columns: 1fr; } }
.mh-col { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.mh-card { background: #fff; border: 1px solid var(--mid-gray); border-radius: 14px; padding: 16px 18px; }
.mh-h { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
.mh-h h3 { display: flex; align-items: center; gap: 8px; margin: 0; font-family: var(--head-font); font-size: 16px; }
.mh-h h3 svg { width: 15px; height: 15px; margin: 0; fill: var(--primary-color); }
.seg { display: inline-flex; background: var(--light-gray); border-radius: 8px; padding: 3px; }
.seg button { border: none; background: none; font-family: inherit; font-size: 12.5px; font-weight: 600; color: var(--font-dark-gray); padding: 5px 10px; border-radius: 6px; cursor: pointer; }
/* Active side looks like the header's Admin | Standard switch */
.seg button:hover:not(.on) { color: var(--dark-gray); }
.seg button.on { background: var(--primary-color); color: #fff; box-shadow: 0 1px 3px rgba(44,41,38,.2); }
.mh-day { margin-top: 10px; }
.mh-dl { font-size: 12px; font-weight: 700; color: var(--font-dark-gray); padding: 4px 0 6px; border-bottom: 1px solid var(--mid-gray); }
.mh-dl span { font-weight: 500; color: var(--font-mid-gray); margin-left: 6px; }
.mh-dl.today { color: var(--primary-color); }
.mh-row { display: flex; align-items: center; gap: 12px; padding: 9px 0; border-bottom: 1px solid var(--light-gray); }
.mh-row-ended { opacity: .55; }
.mh-time { width: 72px; font-family: var(--head-font); font-weight: 700; font-size: 13.5px; color: var(--dark-gray); flex-shrink: 0; }
.mh-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.mh-mm { flex: 1; min-width: 0; text-align: left; }
.mh-mm b { display: block; font-size: 14px; color: var(--dark-gray); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mh-mm span { font-size: 12px; color: var(--font-dark-gray); }
.mh-open { margin: 0; padding: 5px 12px; font-size: 12.5px; flex-shrink: 0; }
.mh-none { font-size: 12.5px; color: var(--font-mid-gray); margin: 8px 0; }
.mh-h2 { font-family: var(--head-font); font-weight: 700; font-size: 16px; margin: 4px 0 -6px; }
.mh-wsgrid { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 12px; }
.mh-ws { display: flex; flex-direction: column; gap: 10px; text-align: left; background: #fff; border: 1px solid var(--mid-gray); border-radius: 14px; padding: 14px 16px; font-family: inherit; color: inherit; cursor: pointer; }
.mh-ws:hover { border-color: var(--font-mid-gray); box-shadow: 0 4px 14px rgba(44,41,38,.06); }
.mh-ws-h { display: flex; align-items: center; gap: 8px; font-size: 15px; }
.mh-ws-h b { font-family: var(--head-font); color: var(--dark-gray); }
.mh-lock { width: 14px; height: 14px; flex-shrink: 0; color: var(--primary-color); }
/* Dots take the lock's 14px, so names line up whichever a row has */
.mh-row .mh-dot { margin: 0 2px; }
.mh-chev { margin-left: auto; width: 16px; height: 16px; fill: var(--font-mid-gray); }
.mh-ws-m { display: flex; flex-wrap: wrap; gap: 5px; }
.mh-ws-m span { font-size: 11.5px; background: var(--light-gray); border-radius: 10px; padding: 2px 9px; color: var(--font-dark-gray); }
.mh-ws-m span.mh-muted { background: none; padding: 0; color: var(--font-mid-gray); }
.mh-ws-m span.mh-more { background: none; color: var(--font-mid-gray); font-weight: 600; }
.mh-ws-s { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; font-size: 12px; color: var(--font-dark-gray); }
.mh-ws-s b { color: var(--dark-gray); }
.mh-next { display: inline-flex; align-items: center; gap: 4px; margin-left: auto; }
.mh-next svg { width: 13px; height: 13px; margin: 0; fill: currentColor; }
.mh-sub { font-size: 12px; font-weight: 700; color: var(--font-dark-gray); margin: 12px 0 4px; }
.mh-sub span { background: var(--light-gray); border-radius: 9px; padding: 0 7px; font-size: 11px; margin-left: 4px; }
.mh-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 8px 0; border: none; border-bottom: 1px solid var(--light-gray); background: none; font-family: inherit; cursor: pointer; }
.mh-item:hover b { color: var(--primary-color); }
.mh-overdue { font-style: normal; font-weight: 700; color: var(--primary-color); }
</style>
